import React from "react";
import { ArrowDownWideNarrow, ArrowUpWideNarrow, ChevronDown, ChevronRight } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import Checkbox from "../ui/Checkbox";
import Tooltip from "../ui/Tooltip";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import {
  useMassPropertiesActions,
  useMassPropertiesAutoRecomputeQueued,
  useMassPropertiesBodyComMarkersVisible,
  useMassPropertiesIsStale,
  useMassPropertiesRobotComMarkerVisible,
  useMassPropertiesRows,
  useMassPropertiesStatus,
  useMassPropertiesSummary,
} from "../../stores/massPropertiesStore";
import { useBodies, useWorldBodies } from "../../stores/sceneStore";
import { buildBodyRootMap } from "../../utils/bodyHierarchy";

const formatNumber = (value, digits = 4) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1000 || (Math.abs(n) > 0 && Math.abs(n) < 0.001)) {
    return n.toExponential(3);
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const formatVector = (vector) =>
  Array.isArray(vector) ? vector.map((value) => formatNumber(value, 4)).join(", ") : "0, 0, 0";

const basename = (name) => {
  const text = String(name || "").trim();
  if (!text) return "";
  const parts = text.split("/").filter(Boolean);
  return parts[parts.length - 1] || text;
};

const formatQuaternion = (quaternion) =>
  Array.isArray(quaternion)
    ? quaternion.map((value) => formatNumber(value, 5)).join(", ")
    : "1, 0, 0, 0";

const quaternionToMatrix = (quaternion) => {
  if (!Array.isArray(quaternion) || quaternion.length < 4) return null;
  const [w, x, y, z] = quaternion.map((value) => Number(value));
  if (![w, x, y, z].every(Number.isFinite)) return null;

  return [
    [1 - 2 * (y * y + z * z), 2 * (x * y - z * w), 2 * (x * z + y * w)],
    [2 * (x * y + z * w), 1 - 2 * (x * x + z * z), 2 * (y * z - x * w)],
    [2 * (x * z - y * w), 2 * (y * z + x * w), 1 - 2 * (x * x + y * y)],
  ];
};

const computeBodyFrameInertiaTensor = (diagInertia, inertialQuat) => {
  if (!Array.isArray(diagInertia) || diagInertia.length < 3) return null;
  const diagonal = diagInertia.map((value) => Number(value));
  if (!diagonal.every(Number.isFinite)) return null;
  const rotation = quaternionToMatrix(inertialQuat);
  if (!rotation) return null;

  return rotation.map((row) =>
    rotation.map((column) =>
      row.reduce((sum, value, index) => sum + value * diagonal[index] * column[index], 0)
    )
  );
};

const buildWorldBodyNameByUuid = (worldBodies) => {
  const names = {};
  (worldBodies || []).forEach((worldBody) => {
    if (!worldBody) return;
    if (typeof worldBody === "string") {
      names[worldBody] = "";
      return;
    }
    if (worldBody.uuid) names[worldBody.uuid] = worldBody.name || "";
  });
  return names;
};

const SLOW_RECOMPUTE_NOTICE_MS = 2200;

const MassPropertiesPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const status = useMassPropertiesStatus();
  const isStale = useMassPropertiesIsStale();
  const autoRecomputeQueued = useMassPropertiesAutoRecomputeQueued();
  const rows = useMassPropertiesRows();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const showRobotComMarker = useMassPropertiesRobotComMarkerVisible();
  const showBodyComMarkers = useMassPropertiesBodyComMarkersVisible();
  const { totalMass, totalCom, computedAt, error } = useMassPropertiesSummary();
  const { compute, setComMarkerVisibility } = useMassPropertiesActions();
  const isPhysicsRunning = usePhysicsStore((state) => state.isRunning || state.isPaused);
  const selectBody = useSelectionStore((state) => state.selectBody);
  const [sort, setSort] = React.useState({ key: "subtreeMass", direction: "desc" });
  const [showSlowRecomputeNotice, setShowSlowRecomputeNotice] = React.useState(false);
  const [expandedRows, setExpandedRows] = React.useState(() => new Set());
  const totalMassValue = Number(totalMass) || 0;
  const hasSceneBodies = Object.keys(bodies || {}).length > 0;

  const bodyLabelsByUuid = React.useMemo(() => {
    const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
    const worldBodyNameByUuid = buildWorldBodyNameByUuid(worldBodies);
    return Object.fromEntries(
      rows.map((row) => {
        const body = bodies?.[row.bodyUuid];
        const rootUuid = rootMap[row.bodyUuid];
        const rootBody = rootUuid ? bodies?.[rootUuid] : null;
        const objectName =
          (rootUuid && worldBodyNameByUuid[rootUuid]) ||
          basename(rootBody?.name) ||
          (row.displayName.includes("/") ? row.displayName.split("/")[0] : "");
        const bodyName = basename(body?.name || row.displayName || row.name);
        const contextName = objectName && objectName !== bodyName ? objectName : "";
        return [
          row.bodyUuid,
          {
            rootUuid: rootUuid || row.bodyUuid,
            bodyName: bodyName || row.displayName || row.name,
            contextName,
            fullName: contextName ? `${bodyName} · ${contextName}` : bodyName,
          },
        ];
      })
    );
  }, [bodies, rows, worldBodies]);

  const groupedRows = React.useMemo(() => {
    const direction = sort.direction === "asc" ? 1 : -1;
    const valueForSort = (row) => {
      if (sort.key === "name") {
        const label = bodyLabelsByUuid[row.bodyUuid];
        return label?.bodyName || row.displayName;
      }
      if (sort.key === "percent") {
        return totalMassValue > 0 ? (row.subtreeMass / totalMassValue) * 100 : 0;
      }
      return Number(row[sort.key]) || 0;
    };
    const sortRows = (groupRows) =>
      [...groupRows].sort((a, b) => {
        const av = valueForSort(a);
        const bv = valueForSort(b);
        if (typeof av === "string" || typeof bv === "string") {
          return String(av).localeCompare(String(bv)) * direction;
        }
        return (av - bv) * direction;
      });
    const groupsByRoot = new Map();

    rows.forEach((row) => {
      const label = bodyLabelsByUuid[row.bodyUuid] || {};
      const rootUuid = label.rootUuid || row.bodyUuid;
      if (!groupsByRoot.has(rootUuid)) {
        groupsByRoot.set(rootUuid, {
          rootUuid,
          objectName: label.contextName || label.bodyName || row.displayName,
          rows: [],
        });
      }
      groupsByRoot.get(rootUuid).rows.push(row);
    });

    const worldOrder = new Map(
      (worldBodies || [])
        .map((worldBody, index) => [
          typeof worldBody === "string" ? worldBody : worldBody?.uuid,
          index,
        ])
        .filter(([uuid]) => uuid)
    );

    return Array.from(groupsByRoot.values())
      .map((group) => {
        const groupMass = group.rows.reduce((sum, row) => sum + (Number(row.mass) || 0), 0);
        return {
          ...group,
          mass: groupMass,
          percent: totalMassValue > 0 ? (groupMass / totalMassValue) * 100 : 0,
          rows: sortRows(group.rows),
        };
      })
      .sort((a, b) => {
        const ai = worldOrder.has(a.rootUuid)
          ? worldOrder.get(a.rootUuid)
          : Number.MAX_SAFE_INTEGER;
        const bi = worldOrder.has(b.rootUuid)
          ? worldOrder.get(b.rootUuid)
          : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return String(a.objectName).localeCompare(String(b.objectName));
      });
  }, [bodyLabelsByUuid, rows, sort, totalMassValue, worldBodies]);

  const setSortKey = React.useCallback((key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }, []);

  const toggleExpandedRow = React.useCallback((bodyUuid) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(bodyUuid)) {
        next.delete(bodyUuid);
      } else {
        next.add(bodyUuid);
      }
      return next;
    });
  }, []);

  const SortIcon = sort.direction === "asc" ? ArrowUpWideNarrow : ArrowDownWideNarrow;

  React.useEffect(() => {
    if (!open || isPhysicsRunning || status === "computing") return;
    if (status === "idle" || (status === "ready" && isStale && !autoRecomputeQueued)) {
      void compute();
    }
  }, [autoRecomputeQueued, compute, isPhysicsRunning, isStale, open, status]);

  React.useEffect(() => {
    const recomputePending = isStale && (autoRecomputeQueued || status === "computing");
    if (!recomputePending) {
      setShowSlowRecomputeNotice(false);
      return undefined;
    }
    const timeout = window.setTimeout(
      () => setShowSlowRecomputeNotice(true),
      SLOW_RECOMPUTE_NOTICE_MS
    );
    return () => window.clearTimeout(timeout);
  }, [autoRecomputeQueued, isStale, status]);

  const recomputePending = isStale && (autoRecomputeQueued || status === "computing");
  const showSubtleRecomputePending = recomputePending && !showSlowRecomputeNotice;
  const showStaleWarning = isStale && (!recomputePending || showSlowRecomputeNotice);
  const showInitialPanelLoading =
    open &&
    hasSceneBodies &&
    !computedAt &&
    !error &&
    !isPhysicsRunning &&
    (status === "idle" || status === "computing");
  const showPerObjectSummary = groupedRows.length > 1;

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Mass & Balance"
      titleIcon={titleIcon}
      initialSize={{ width: 520, height: 560 }}
      minWidth={420}
      minHeight={340}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden text-gray-200">
        {showInitialPanelLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-950/80 backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-3 rounded border border-gray-700 bg-gray-900/95 px-5 py-4 text-center shadow-xl">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-600 border-t-gray-100" />
              <div>
                <div className="text-sm font-medium text-gray-100">Computing mass properties</div>
                <div className="mt-1 text-[11px] text-gray-400">
                  Compiling the current scene with MuJoCo...
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="space-y-2.5 p-3">
          {showSubtleRecomputePending ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-gray-200" />
              Updating mass properties...
            </div>
          ) : null}
          {showStaleWarning ? (
            <div className="rounded border border-amber-700/70 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
              {recomputePending
                ? "Mass properties are taking longer than usual to recompute. Values may be stale until the update finishes."
                : "Scene changed since the last compute. Values will refresh automatically when possible."}
            </div>
          ) : null}
          {status === "computing" && !isStale && !showInitialPanelLoading ? (
            <div className="rounded border border-sky-800/70 bg-sky-950/30 px-3 py-2 text-xs text-sky-100">
              Computing mass properties...
            </div>
          ) : null}
          {status === "error" && error ? (
            <div className="rounded border border-red-800/70 bg-red-950/30 px-3 py-2 text-xs text-red-100">
              {error}
            </div>
          ) : null}
          {isPhysicsRunning ? (
            <div className="rounded border border-sky-800/70 bg-sky-950/30 px-3 py-2 text-xs text-sky-100">
              Mass properties are disabled while physics is running.
            </div>
          ) : null}

          {showPerObjectSummary ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {groupedRows.map((group) => (
                <div
                  key={group.rootUuid}
                  className="rounded border border-gray-700 bg-gray-900/60 px-3 py-2.5"
                >
                  <div className="truncate text-[10px] uppercase tracking-wide text-gray-300">
                    {group.objectName}
                  </div>
                  <div className="mt-1 text-base font-semibold text-gray-100">
                    {formatNumber(group.mass, 5)} kg
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <div className="rounded border border-gray-700 bg-gray-900/60 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wide text-gray-300">Total mass</div>
                <div className="mt-1 text-base font-semibold text-gray-100">
                  {formatNumber(totalMassValue, 5)} kg
                </div>
              </div>
            </div>
          )}
          <div className="space-y-1 text-[11px] text-gray-300">
            <div>
              {computedAt
                ? `Computed ${new Date(computedAt).toLocaleTimeString()}`
                : "Not computed yet"}
            </div>
            {showPerObjectSummary ? (
              <div className="font-mono text-gray-200">
                Total mass: {formatNumber(totalMassValue, 5)} kg
              </div>
            ) : null}
            <div className="font-mono text-gray-200">
              Center of Mass position: {formatVector(totalCom)}
            </div>
          </div>
          <div className="rounded border border-gray-700 bg-gray-900/50 px-3 py-2">
            <div className="mb-1.5 text-[10px] uppercase tracking-wide text-gray-300">
              Center of Mass Markers
            </div>
            <div className="space-y-0.5">
              <Checkbox
                label="Show per-robot/object"
                checked={showRobotComMarker}
                className="!gap-1.5 px-0 py-0 text-[9px] leading-3 text-gray-100 [&>div>div]:h-3 [&>div>div]:w-3 [&>div>div]:rounded-sm [&>div>div>svg]:h-2.5 [&>div>div>svg]:w-2.5 [&>span]:leading-3"
                onChange={(event) => setComMarkerVisibility("robot", event.currentTarget.checked)}
              />
              <Checkbox
                label="Show per-body"
                checked={showBodyComMarkers}
                className="!gap-1.5 px-0 py-0 text-[9px] leading-3 text-gray-100 [&>div>div]:h-3 [&>div>div]:w-3 [&>div>div]:rounded-sm [&>div>div>svg]:h-2.5 [&>div>div>svg]:w-2.5 [&>span]:leading-3"
                onChange={(event) => setComMarkerVisibility("body", event.currentTarget.checked)}
              />
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-300">
            Body Mass Breakdown
          </div>
          {groupedRows.map((group) => (
            <section
              key={group.rootUuid}
              className="overflow-hidden rounded border border-gray-700 bg-gray-950/70"
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-700 bg-gray-800 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-gray-300">Object</div>
                  <Tooltip content={group.objectName} fullWidth>
                    <div className="truncate text-xs font-semibold text-white">
                      {group.objectName}
                    </div>
                  </Tooltip>
                </div>
                <div className="shrink-0 text-right font-mono text-[11px] text-gray-200">
                  <div>{formatNumber(group.mass, 5)} kg</div>
                  <div className="text-gray-400">{formatNumber(group.percent, 2)}% total</div>
                </div>
              </div>

              <table className="w-full border-collapse text-left text-[11px]">
                <thead className="bg-gray-900 text-gray-300">
                  <tr className="border-b border-gray-800">
                    {[
                      ["name", "Body"],
                      ["mass", "Mass"],
                      ["subtreeMass", "Subtree"],
                      ["percent", "% total"],
                      ["diagInertia", "Diag inertia"],
                    ].map(([key, label]) => (
                      <th key={key} className="px-2 py-1.5 font-medium">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-gray-100"
                          onClick={() => key !== "diagInertia" && setSortKey(key)}
                          disabled={key === "diagInertia"}
                        >
                          {label}
                          {sort.key === key ? <SortIcon className="h-3 w-3" /> : null}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => {
                    const percent =
                      totalMassValue > 0 ? (row.subtreeMass / totalMassValue) * 100 : 0;
                    const isExpanded = expandedRows.has(row.bodyUuid);
                    const label = bodyLabelsByUuid[row.bodyUuid] || {
                      bodyName: row.displayName,
                      contextName: "",
                      fullName: row.displayName,
                    };
                    const bodyFrameTensor = computeBodyFrameInertiaTensor(
                      row.diagInertia,
                      row.inertialQuat
                    );
                    return (
                      <React.Fragment key={row.bodyUuid}>
                        <tr
                          className="cursor-pointer border-b border-gray-800/70 text-gray-200 hover:bg-gray-800/50"
                          onClick={() => selectBody(row.bodyUuid)}
                        >
                          <td className="max-w-[150px] truncate px-2 py-1.5 text-gray-100">
                            <Tooltip content={label.fullName} fullWidth>
                              <button
                                type="button"
                                className="inline-flex max-w-full items-center gap-1 text-left hover:text-white"
                                aria-expanded={isExpanded}
                                aria-label={`${
                                  isExpanded ? "Hide" : "Show"
                                } advanced details for ${label.fullName}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleExpandedRow(row.bodyUuid);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-3 w-3 shrink-0 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 shrink-0 text-gray-400" />
                                )}
                                <span className="truncate font-medium text-gray-100">
                                  {label.bodyName}
                                </span>
                              </button>
                            </Tooltip>
                          </td>
                          <td className="px-2 py-1.5 font-mono">{formatNumber(row.mass, 5)}</td>
                          <td className="px-2 py-1.5 font-mono">
                            {formatNumber(row.subtreeMass, 5)}
                          </td>
                          <td className="px-2 py-1.5 font-mono">{formatNumber(percent, 2)}%</td>
                          <td className="px-2 py-1.5 font-mono text-[10px] text-gray-300">
                            {formatVector(row.diagInertia)}
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="border-b border-gray-800/70 bg-gray-950/60 text-gray-300">
                            <td colSpan={5} className="px-2 py-2">
                              <div className="grid gap-2 text-[10px] sm:grid-cols-2">
                                <div className="rounded border border-gray-800 bg-gray-900/60 p-2">
                                  <div className="mb-1 uppercase tracking-wide text-gray-400">
                                    Center of Mass
                                  </div>
                                  <div className="space-y-0.5 font-mono text-gray-200">
                                    <div>Local: {formatVector(row.localCom)}</div>
                                    <div>World: {formatVector(row.worldCom)}</div>
                                    <div>Subtree: {formatVector(row.subtreeCom)}</div>
                                  </div>
                                </div>
                                <div className="rounded border border-gray-800 bg-gray-900/60 p-2">
                                  <div className="mb-1 uppercase tracking-wide text-gray-400">
                                    Inertia
                                  </div>
                                  <div className="space-y-0.5 font-mono text-gray-200">
                                    <div>Principal diagonal: {formatVector(row.diagInertia)}</div>
                                    <div>
                                      Inertial frame quat: {formatQuaternion(row.inertialQuat)}
                                    </div>
                                  </div>
                                </div>
                                <div className="rounded border border-gray-800 bg-gray-900/60 p-2">
                                  <div className="mb-1 uppercase tracking-wide text-gray-400">
                                    Mass Breakdown
                                  </div>
                                  <div className="space-y-0.5 font-mono text-gray-200">
                                    <div>Body: {formatNumber(row.mass, 5)} kg</div>
                                    <div>Subtree: {formatNumber(row.subtreeMass, 5)} kg</div>
                                    <div>Total share: {formatNumber(percent, 2)}%</div>
                                  </div>
                                </div>
                                <div className="rounded border border-gray-800 bg-gray-900/60 p-2">
                                  <div className="mb-1 uppercase tracking-wide text-gray-400">
                                    Body-Frame Inertia Tensor
                                  </div>
                                  {bodyFrameTensor ? (
                                    <div className="space-y-0.5 font-mono text-gray-200">
                                      {bodyFrameTensor.map((tensorRow, index) => (
                                        <div key={index}>[{formatVector(tensorRow)}]</div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-gray-500">Unavailable</div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}
          {rows.length === 0 ? (
            <div className="rounded border border-gray-800 bg-gray-950/70 px-2 py-6 text-center text-[11px] text-gray-500">
              Compute mass properties to populate the body breakdown.
            </div>
          ) : null}
        </div>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(MassPropertiesPanel);
